import React, {useState, useEffect, useRef} from 'react';
import {
    Box,
    Checkbox,
    FormControlLabel,
    FormGroup,
    Typography,
    Divider,
    Slider,
    IconButton,
    TextField,
} from '@mui/material';
import {SettingsForm} from './types';
import CloseIcon from '@mui/icons-material/Close';
import {getPreviewUrl} from '~/shared/utils/uploadImage';
import {CropImageInput} from '~/shared/ui/inputs/CropImageInput';

const RangeSlider: React.FC<any> = ({
                                        label,
                                        templateMax,
                                        globalMax,
                                        currentMax,
                                        currentMin,
                                        onChange,
                                    }) => {
    const effectiveMax = Math.min(templateMax, globalMax);
    const [localRange, setLocalRange] = useState<number[]>([
        Math.min(currentMin, effectiveMax),
        Math.min(currentMax, effectiveMax)
    ]);

    // sync with outside props
    useEffect(() => {
        setLocalRange([
            Math.min(currentMin, effectiveMax),
            Math.min(currentMax, effectiveMax)
        ]);
    }, [currentMin, currentMax, effectiveMax]);

    const handleChange = (_: Event, newValue: number | number[]) => {
        if (!Array.isArray(newValue)) return;
        const newMin = Math.min(newValue[0], effectiveMax);
        const newMax = Math.min(newValue[1], effectiveMax);
        setLocalRange([newMin, newMax]);
        onChange(newMin, newMax);
    };

    return (
        <Box sx={{width: '100%', px: 0, py: 1}}>
            <Typography variant="subtitle1" gutterBottom>
                {label}: от {localRange[0]} до {localRange[1]}
            </Typography>
            <Slider
                value={localRange}
                onChange={handleChange}
                min={1}
                max={effectiveMax}
                disableSwap
                valueLabelDisplay="auto"
                sx={{
                    '& .MuiSlider-valueLabel': {
                        backgroundColor: 'primary.main',
                        borderRadius: 1,
                        py: 0.2,
                    },
                }}
            />
        </Box>
    );
};

export const Settings: React.FC<any> = ({
                                            settings,
                                            setSettings,
                                            domains,
                                            settingsMode,
                                            images,
                                            setImages,
                                            suffixPreview,
                                            suffixPreviewHint,
                                        }) => {
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

    const handleChange = (field: keyof SettingsForm, value: any) => {
        setSettings((prev: any) => ({...prev, [field]: value}));
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
    };

    const handleLogoCountChange = (_: Event, newValue: number | number[]) => {
        const val = newValue as number;
        setSettings((prev: any) => ({
            ...prev,
            logo: {min: val, max: val}
        }));
    };

    const handleLogoUpload = (index: number, files: File[]) => {
        if (!files || !files.length) return;
        const newImages = [...(images || [])];
        newImages[index] = files[0];
        setImages(newImages);
    };

    const handleLogoRemove = (index: number) => {
        const newImages = [...(images || [])];
        newImages[index] = undefined;
        setImages(newImages);
    };

    return (
        <Box sx={{maxWidth: 600, margin: 'auto'}}>
            <form onSubmit={handleSubmit}>
                <Box
                    sx={{
                        mt: 1.25,
                        p: 1.25,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1.5,
                        bgcolor: 'background.paper',
                    }}
                >
                    <Typography variant="subtitle2" fontWeight={700}>
                        Business name{domains?.length > 1 ? ' по доменам' : ''}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{mt: 0.5, display: 'block'}}>
                        {domains?.length > 1
                            ? 'Для каждого домена подставляется свое автоимя. При необходимости можно отредактировать каждое значение вручную.'
                            : 'По умолчанию заполняется из домена. Можно редактировать вручную; если оставить пустым, имя снова будет взято из домена.'}
                    </Typography>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: {
                                xs: 'minmax(0, 1fr)',
                                md: 'repeat(2, minmax(0, 1fr))',
                            },
                            gap: 1.25,
                            maxHeight: domains?.length > 4 ? 240 : 'none',
                            overflowY: domains?.length > 4 ? 'auto' : 'visible',
                            pr: domains?.length > 4 ? 0.5 : 0,
                        }}
                    >
                        {domains?.map((domain: string) => (
                            <Box
                                key={domain}
                                sx={{
                                    minWidth: 0,
                                    p: 1,
                                    borderRadius: 1,
                                    bgcolor: 'action.hover',
                                }}
                            >
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                        display: 'block',
                                        mb: 0.5,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                    title={domain}
                                >
                                    {domain}
                                </Typography>
                                <TextField
                                    fullWidth
                                    value={settings.business_names?.[domain] || ''}
                                    onChange={e => {
                                        const nextValue = e.target.value;

                                        setSettings((prev: any) => ({
                                            ...prev,
                                            business_names: {
                                                ...(prev.business_names || {}),
                                                [domain]: nextValue,
                                            },
                                        }));
                                    }}
                                    placeholder="BestService LLC"
                                    size="small"
                                    inputProps={{maxLength: 25}}
                                    sx={{
                                        '& .MuiInputBase-input': {
                                            py: 1,
                                        },
                                    }}
                                />
                            </Box>
                        ))}
                    </Box>
                </Box>

                <Box
                    sx={{
                        mt: 1.25,
                        p: 1.25,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1.5,
                        bgcolor: 'background.paper',
                    }}
                >
                    <Box
                        sx={{
                            mb: 1.25,
                            p: 1.25,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1.5,
                            bgcolor: 'action.hover',
                        }}
                    >
                        <FormGroup sx={{mb: 0.5}}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={settings.append_tag}
                                        onChange={e => handleChange('append_tag', e.target.checked)}
                                    />
                                }
                                label="Автоматически вставлять тег конверсии"
                            />
                        </FormGroup>
                        <Typography variant="subtitle2" fontWeight={700}>
                            Тег конверсии
                        </Typography>
                        <TextField
                            fullWidth
                            value={settings.custom_utm}
                            onChange={e => handleChange('custom_utm', e.target.value)}
                            placeholder="AW-123456789/AbCdEfGhIjK"
                            size="small"
                            sx={{mt: 0.5}}
                        />
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{mt: 0.35, display: 'block', fontSize: '0.72rem', lineHeight: 1.35}}
                        >
                            {settings.append_tag
                                ? 'Если поле пустое, тег будет подставлен автоматически. Если заполнено, значение из поля имеет приоритет.'
                                : 'Автоподстановка выключена. Если поле заполнено, будет использовано значение из поля; если пустое, тег не добавится.'}
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: {
                                xs: 'minmax(0, 1fr)',
                                md: 'repeat(2, minmax(0, 1fr))',
                            },
                            gap: 1.25,
                            alignItems: 'start',
                        }}
                    >
                        <Box>
                            <Typography variant="subtitle2" fontWeight={700}>
                                Final URL suffix
                            </Typography>
                            <TextField
                                fullWidth
                                value={settings.final_url_suffix}
                                onChange={e => handleChange('final_url_suffix', e.target.value)}
                                placeholder="utm_source=google&utm_medium=cpc"
                                size="small"
                                sx={{mt: 0.5}}
                            />
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{mt: 0.35, display: 'block', fontSize: '0.72rem', lineHeight: 1.35}}
                            >
                                Будет добавлен после системных параметров `a` и `tag`.
                            </Typography>
                        </Box>
                        <Box sx={{gridColumn: '1 / -1'}}>
                            <Typography variant="subtitle2" fontWeight={700}>
                                Предпросмотр итогового suffix
                            </Typography>
                            <Box
                                sx={{
                                    mt: 0.5,
                                    px: 1.25,
                                    py: 1,
                                    borderRadius: 1,
                                    bgcolor: 'action.hover',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontFamily: 'monospace',
                                        fontSize: '0.9rem',
                                        wordBreak: 'break-all',
                                    }}
                                >
                                    {suffixPreview || ''}
                                </Typography>
                            </Box>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{mt: 0.35, display: 'block', fontSize: '0.72rem', lineHeight: 1.35}}
                            >
                                {suffixPreviewHint ||
                                    'Так итоговый Final URL suffix будет выглядеть при отправке в Google Ads.'}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
                <br/>

                <Box sx={{width: '100%', px: 0, py: 1}}>
                    <Typography variant="subtitle1" gutterBottom>
                        Количество логотипов: {settings.logo.min}
                    </Typography>
                    <Slider
                        value={settings.logo.min}
                        onChange={handleLogoCountChange}
                        min={1}
                        max={5}
                        valueLabelDisplay="auto"
                        sx={{
                            '& .MuiSlider-valueLabel': {
                                backgroundColor: 'primary.main',
                                borderRadius: 1,
                                py: 0.2,
                            },
                        }}
                    />
                </Box>

                <Box
                    display="grid"
                    gridTemplateColumns="repeat(5, minmax(0, 1fr))"
                    gap={2}
                    mb={2}
                >
                    {Array.from({length: settings.logo.min}).map((_, index) => {
                        const value = images && images[index] ? images[index] : undefined;
                        const src = value ? getPreviewUrl(value) : '';
                        return (
                            <Box key={index} display="flex" flexDirection="column" gap={1} alignItems="center">
                                {!src && (
                                    <CropImageInput
                                        name=""
                                        titleButton={`Лого ${index + 1}`}
                                        title=""
                                        aspect={1}
                                        onChange={(file: any) => handleLogoUpload(index, [file])}
                                        onNameChange={() => {
                                        }}
                                        buttonVariant="outlined"
                                        buttonSx={{
                                            width: '100%',
                                            height: 40,
                                            border: '1px dashed',
                                            borderColor: 'primary.main',
                                            borderRadius: 2,
                                            bgcolor: 'transparent',
                                            color: 'primary.main',
                                            textTransform: 'none',
                                            '&:hover': {borderColor: 'primary.main', bgcolor: 'action.hover'},
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                        buttonLabelSx={{variant: 'caption'}}
                                    />
                                )}
                                {src ? (
                                    <Box sx={{position: 'relative', width: 64, height: 64, alignSelf: 'center'}}>
                                        <Box
                                            component="img"
                                            src={src}
                                            sx={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover',
                                                borderRadius: 1,
                                                border: '1px solid',
                                                borderColor: 'divider',
                                            }}
                                        />
                                        <IconButton
                                            size="small"
                                            onClick={() => handleLogoRemove(index)}
                                            sx={{
                                                position: 'absolute',
                                                top: -8,
                                                right: -8,
                                                bgcolor: 'background.paper',
                                                boxShadow: 1,
                                                '&:hover': {bgcolor: 'background.paper'},
                                                p: 0.5,
                                            }}
                                        >
                                            <CloseIcon fontSize="small"/>
                                        </IconButton>
                                    </Box>
                                ) : null}
                            </Box>
                        );
                    })}
                </Box>

                <RangeSlider
                    label="Заголовки"
                    min={1}
                    templateMax={settingsMode.headlines}
                    globalMax={5}
                    currentMin={settings.headlines.min}
                    currentMax={settings.headlines.max}
                    onChange={(min: number, max: number) => {
                        setSettings((prev: any) => ({
                            ...prev,
                            headlines: {min, max}
                        }));
                    }}
                />
                <RangeSlider
                    label="Описания"
                    min={1}
                    templateMax={settingsMode.descriptions}
                    globalMax={5}
                    currentMin={settings.descriptions.min}
                    currentMax={settings.descriptions.max}
                    onChange={(min: number, max: number) => {
                        setSettings((prev: any) => ({
                            ...prev,
                            descriptions: {min, max}
                        }));
                    }}
                />

                {/* dynamic fields based on mode */}
                {settings.mode === 'marketing_image' ? (
                    <Box>
                        <RangeSlider
                            label="Маркетинговые изображения"
                            min={1}
                            templateMax={settingsMode.marketing_image}
                            globalMax={5}
                            currentMin={settings.marketing_image.min}
                            currentMax={settings.marketing_image.max}
                            onChange={(min: number, max: number) => {
                                setSettings((prev: any) => ({
                                    ...prev,
                                    marketing_image: {min, max}
                                }));
                            }}
                        />
                        <Typography variant="caption" color="text.secondary">
                            Доступно: 1:1 — {settingsMode.marketing_image_square || 0}, 1.91:1
                            — {settingsMode.marketing_image_landscape || 0}, 4:5
                            — {settingsMode.marketing_image_portrait || 0}. Нужно минимум одно 1:1 или 1.91:1.
                        </Typography>
                    </Box>
                ) : (
                    <RangeSlider
                        onChange={(min: number, max: number) => {
                            setSettings((prev: any) => ({
                                ...prev,
                                marketing_link: {min, max}
                            }));
                        }}
                        label={'Маркетинговые ссылки'}
                        min={1}
                        max={settingsMode.marketing_link}
                        currentMin={settings.marketing_link.min}
                        currentMax={Math.min(settings.marketing_link.max, 5)}
                    />
                )}
            </form>
        </Box>
    );
};
